import { notificationQueryKeys, useGetAllNotificationInfinite, useReadAllNotification, useReadNotification } from '@/api/notification';
import { useSocket } from '@/context/socketContext';
import { useToast } from '@/hooks/use-toast';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { openWindow } from '@/redux/slice/windowSlice';
import { emptyFunction } from '@/utils/common/common';
import { dateUtils } from '@/utils/dateUtils';
import { Pagination, SocketResponse } from '@/utils/types/common';
import { GetAllNotificationResponse, NotificationData, NotificationProps } from '@/utils/types/notification';
import { SocketUser } from '@/utils/types/window';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { FiCheck, FiEye } from 'react-icons/fi';
import { RiNotification2Fill } from 'react-icons/ri';
import { useDispatch } from 'react-redux';
const Notification: React.FC<NotificationProps> = ({
  isNotificationOpen,
  setIsNotificationOpen
}) => {
  const {
    socket,
    isConnected,
    joinModule,
    leaveModule
  } = useSocket();
  const user = useCurrentUser();
  const {
    toast
  } = useToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
    refetch
  } = useGetAllNotificationInfinite({
    limit: 5
  });
  const {
    mutate: markAsRead
  } = useReadNotification();
  const {
    mutate: markAllAsRead,
    isPending: isMarkingAllAsRead
  } = useReadAllNotification();
  const notificationList: NotificationData[] = data?.pages?.flatMap(page => page.data.notifications) ?? [];
  const totalCount: number = data?.pages?.[0]?.pagination?.totalRecords ?? 0;
  const hasUnreadNotifications = useMemo(() => {
    return notificationList.some(notification => !notification.readStatus);
  }, [notificationList]);
  const timeAgo = (dateString: string): string => {
    const date = dateUtils.parse.safe(dateString);
    if (!date) return 'Unknown time';
    const now = dateUtils.now();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (seconds < 60) return 'a few seconds ago';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  };
  const toggleNotification = useCallback((): void => {
    setIsNotificationOpen(!isNotificationOpen);
  }, [isNotificationOpen, setIsNotificationOpen]);
  const handleViewMore = useCallback((notificationId: string) => {
    const notification = notificationList.find(n => n.notificationId === notificationId);
    if (!notification) {
      return;
    }
    const title = `Notification - ${notification.moduleName.replace(/_/g, ' ')}`;
    dispatch(openWindow({
      title: title,
      componentKey: 'viewNotification',
      id: null,
      winData: {
        message: notification?.message.toString() ?? '',
        datetime: notification?.createdAt.toString() ?? ''
      }
    }));
    setIsNotificationOpen(!isNotificationOpen);
  }, [notificationList, dispatch, isNotificationOpen, setIsNotificationOpen]);
  const updateNotifications = useCallback((notifications: NotificationData[], notificationId: string): NotificationData[] => {
    return notifications.map(notification => notification.notificationId === notificationId ? {
      ...notification,
      readStatus: true
    } : notification);
  }, []);
  const updatePagination = useCallback((pagination: Pagination | undefined) => {
    if (!pagination) return undefined;
    return {
      ...pagination,
      totalRecords: pagination.totalRecords - 1
    };
  }, []);
  const updatePages = useCallback((pages: GetAllNotificationResponse[], notificationId: string): GetAllNotificationResponse[] => {
    return pages.map(page => ({
      ...page,
      data: {
        ...page.data,
        notifications: updateNotifications(page.data.notifications, notificationId)
      },
      pagination: updatePagination(page.pagination)
    }));
  }, [updateNotifications, updatePagination]);
  const handleSuccess = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: notificationQueryKeys.lists()
    });
  }, [queryClient]);
  const handleError = useCallback((error: unknown) => {
    queryClient.invalidateQueries({
      queryKey: notificationQueryKeys.lists()
    });
    toast({
      title: 'Error',
      description: (error as Error)?.message || 'Failed to mark notification as read',
      variant: 'destructive'
    });
  }, [queryClient, toast]);
  const handleMarkNotification = useCallback((notificationId: string): void => {
    type NotificationInfiniteQueryKey = [...ReturnType<typeof notificationQueryKeys.lists>, 'infinite', {
      limit: number;
    }];
    const queryKey: NotificationInfiniteQueryKey = [...notificationQueryKeys.lists(), 'infinite', {
      limit: 5
    }];
    queryClient.setQueryData<InfiniteData<GetAllNotificationResponse, number>>(queryKey, oldData => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        pages: updatePages(oldData.pages, notificationId)
      };
    });
    markAsRead({
      notificationId
    }, {
      onSuccess: handleSuccess,
      onError: handleError
    });
  }, [markAsRead, queryClient, updatePages, handleSuccess, handleError]);
  const updateAllNotificationsAsRead = useCallback((oldData: InfiniteData<GetAllNotificationResponse, number>) => {
    return {
      ...oldData,
      pages: oldData.pages.map(page => ({
        ...page,
        data: {
          ...page.data,
          notifications: page.data.notifications.map(notification => ({
            ...notification,
            readStatus: true
          }))
        },
        pagination: page.pagination ? {
          ...page.pagination,
          totalRecords: 0
        } : undefined
      }))
    };
  }, []);
  const handleReadAllSuccess = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: notificationQueryKeys.lists()
    });
  }, [queryClient]);
  const handleReadAllError = useCallback((error: unknown) => {
    queryClient.invalidateQueries({
      queryKey: notificationQueryKeys.lists()
    });
    toast({
      title: 'Error',
      description: (error as Error)?.message || 'Failed to mark all notifications as read',
      variant: 'destructive'
    });
  }, [queryClient, toast]);
  const handleReadAll = useCallback((): void => {
    if (!hasUnreadNotifications) {
      return;
    }
    type NotificationInfiniteQueryKey = [...ReturnType<typeof notificationQueryKeys.lists>, 'infinite', {
      limit: number;
    }];
    const queryKey: NotificationInfiniteQueryKey = [...notificationQueryKeys.lists(), 'infinite', {
      limit: 5
    }];
    queryClient.setQueryData<InfiniteData<GetAllNotificationResponse, number>>(queryKey, oldData => {
      if (!oldData) return oldData;
      return updateAllNotificationsAsRead(oldData);
    });
    markAllAsRead(undefined, {
      onSuccess: handleReadAllSuccess,
      onError: handleReadAllError
    });
  }, [hasUnreadNotifications, markAllAsRead, queryClient, updateAllNotificationsAsRead, handleReadAllSuccess, handleReadAllError]);
  const handleScroll = useCallback((): void => {
    const container = scrollContainerRef.current;
    if (!container || !hasNextPage || isFetchingNextPage) return;
    const {
      scrollTop,
      scrollHeight,
      clientHeight
    } = container;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 20;
    if (isNearBottom) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);
  const handleUpdateEvent = useCallback(async (data: SocketResponse): Promise<void> => {
    if (data?.data?.isRefresh === true) {
      queryClient.invalidateQueries({
        queryKey: notificationQueryKeys.lists()
      });
      const {
        data: refetchedData
      } = await refetch();
      const latestNotification = refetchedData?.pages?.[0]?.data?.notifications?.[0];
      if (latestNotification) {
        if (latestNotification?.metadata?.jobDetails?.status === 'COMPLETED') {
          toast({
            title: 'Success',
            description: `Last change in ${latestNotification?.queueError?.replace(/[{}_]/g, ' ').trim()}` + (latestNotification.message ? `: ${latestNotification.message?.split('\n')[0]}` : ''),
            variant: 'success'
          });
        } else if (latestNotification?.metadata?.jobDetails?.status === 'FAILED') {
          toast({
            title: 'Error',
            description: `Last change in ${latestNotification?.queueError?.replace(/[{}_]/g, ' ').trim()} failed` + (latestNotification.message ? `: ${latestNotification.message?.split('\n')[0]}` : ''),
            variant: 'error',
            duration: Infinity
          });
        }
      }
    }
  }, [queryClient, refetch, toast]);
  const setupSocketListeners = useCallback(() => {
    if (!socket || !isConnected || !user) {
      return emptyFunction;
    }
    const channelName = `{Notification}-${user.employeeId}`;
    joinModule(channelName, user as SocketUser);
    socket.on(channelName, handleUpdateEvent);
    return () => {
      socket.off(channelName, handleUpdateEvent);
      leaveModule(channelName, user as SocketUser);
    };
  }, [socket, isConnected, user, joinModule, leaveModule, handleUpdateEvent]);
  const checkInitialLoad = useCallback((): void => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const {
      scrollHeight,
      clientHeight
    } = container;
    const shouldLoadMore = scrollHeight <= clientHeight && hasNextPage && !isLoading && !isFetchingNextPage;
    if (shouldLoadMore) {
      fetchNextPage();
    }
  }, [hasNextPage, isLoading, isFetchingNextPage, fetchNextPage]);
  const setupScrollListeners = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || !isNotificationOpen) return;
    const handleScrollThrottled = () => {
      handleScroll();
    };
    container.addEventListener('scroll', handleScrollThrottled, {
      passive: true
    });
    const timeoutId = setTimeout(checkInitialLoad, 100);
    return () => {
      container.removeEventListener('scroll', handleScrollThrottled);
      clearTimeout(timeoutId);
    };
  }, [handleScroll, checkInitialLoad, isNotificationOpen]);
  useEffect(() => {
    return setupSocketListeners();
  }, [setupSocketListeners]);
  useEffect(() => {
    return setupScrollListeners();
  }, [setupScrollListeners]);
  useEffect(() => {
    if (isError && error) {
      toast({
        title: 'Error',
        description: error.message ?? 'Failed to load notifications',
        variant: 'destructive'
      });
    }
  }, [isError, error, toast]);
  return <div className="notification-dropdown relative" data-sentry-component="Notification" data-sentry-source-file="Notification.tsx">
      <div className="relative">
        <RiNotification2Fill className="h-5 w-5 text-white" data-sentry-element="RiNotification2Fill" data-sentry-source-file="Notification.tsx" />
        {totalCount > 0 && <button className="absolute -right-1 -top-1 flex h-4 w-4 cursor-pointer items-center justify-center rounded-full bg-red-500 text-xs font-semibold text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50" onClick={toggleNotification} onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleNotification();
        }
      }} aria-label={`${totalCount} unread notifications. Click to toggle notification panel.`} type="button">
            {totalCount}
          </button>}
      </div>
      {isNotificationOpen && <div className="absolute right-0 top-[32px] w-60 max-w-xs rounded-lg bg-white p-3 pr-0 text-[--tertiary] shadow">
          <div ref={scrollContainerRef} className="max-h-[300px] space-y-2 overflow-y-auto" style={{
        scrollBehavior: 'smooth',
        overflowY: 'auto',
        height: '300px'
      }}>
            {isLoading && <div className="flex items-center justify-center py-4">
                <div className="text-sm text-gray-500">Loading notifications...</div>
              </div>}

            {notificationList.map((notification, index) => {
          return <div key={`${notification.notificationId}-${index}`} className={`group flex cursor-pointer items-start justify-between rounded-md px-1 py-1 hover:bg-gray-100 ${notification.readStatus ? 'opacity-60' : ''}`}>
                  <div className="min-w-0 flex-1 pr-2">
                    <div className="break-words text-sm font-semibold text-black">
                      {notification.moduleName.replace(/_/g, ' ')}
                    </div>

                    <span className="text-xs font-medium text-[--primary]">{timeAgo(notification.createdAt)}</span>
                  </div>

                  {!notification.readStatus && <>
                      <button onClick={e => {
                e.stopPropagation();
                handleViewMore(notification.notificationId);
              }} title="View" className="ml-3 text-gray-400 transition hover:text-[--primary]">
                        <FiEye className="mr-2 h-4 w-4" />
                      </button>
                      <button className="text-gray-400 transition hover:text-[--primary]" title="Mark as read" onClick={() => handleMarkNotification(notification.notificationId)}>
                        <FiCheck className="h-4 w-4" />
                      </button>
                    </>}
                </div>;
        })}

            {isFetchingNextPage && <div className="flex items-center justify-center py-2">
                <div className="text-xs text-gray-500">Loading more...</div>
              </div>}

            {!hasNextPage && !isFetchingNextPage && notificationList.length > 0 && <div className="flex items-center justify-center py-2">
                <div className="text-xs text-gray-400">No more notifications</div>
              </div>}

            {!isLoading && !isFetchingNextPage && notificationList.length === 0 && <div className="flex items-center justify-center py-4">
                <div className="text-sm text-gray-500">No notifications found</div>
              </div>}
          </div>
          {notificationList.length > 0 && <div className="mr-3 border-t border-gray-100 pt-1">
              <button className="group flex w-full items-center justify-center rounded-md px-3 py-2 text-xs font-medium text-[--primary] transition-all duration-200 hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:cursor-not-allowed disabled:opacity-50" onClick={handleReadAll} disabled={isMarkingAllAsRead ?? !hasUnreadNotifications}>
                {isMarkingAllAsRead ? <>
                    <div className="mr-2 h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
                    Marking all as read...
                  </> : <>
                    <FiCheck className="mr-1 h-3 w-3 transition-transform group-hover:scale-110" />
                    {hasUnreadNotifications ? 'Mark all as read' : 'All notifications read'}
                  </>}
              </button>
            </div>}
        </div>}
    </div>;
};
export default Notification;